import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  CheckCircle2,
  XCircle,
  CircleSlash,
  RefreshCw,
  Loader2,
  Inbox,
  AlertCircle,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';

// ─────────────────────────────── Helpers ───────────────────────────────

const EVENT_BADGE = {
  sent: { className: 'bg-green-100 text-green-800 border-green-200', Icon: CheckCircle2, label: 'Sent' },
  skipped: { className: 'bg-amber-100 text-amber-800 border-amber-200', Icon: CircleSlash, label: 'Skipped' },
  failed: { className: 'bg-red-100 text-red-800 border-red-200', Icon: XCircle, label: 'Failed' },
};

// Friendly labels for the email_type column. New types just fall through to
// a humanized version of the raw key.
const EMAIL_TYPE_LABELS = {
  invoice_reminder: 'Invoice Reminder',
  payment_confirmation: 'Payment Confirmation',
  partial_payment: 'Partial Payment',
  invoice_ready: 'Invoice Ready',
  overdue_reminder: 'Overdue Reminder',
  client_admin_message: 'Admin Message',
  order_confirmed: 'Order Confirmed',
  order_status_update: 'Order Status Update',
  delivery_complete: 'Delivery Complete',
  document_expiring: 'Document Expiring',
  order_deadline_reminder: 'Order Deadline Reminder',
  stripe_payment_success: 'Stripe Payment Success',
  stripe_payment_failed: 'Stripe Payment Failed',
  stripe_ach_initiated: 'ACH Initiated',
  stripe_ach_cleared: 'ACH Cleared',
};

const humanizeEmailType = (raw) => {
  if (!raw) return '—';
  if (EMAIL_TYPE_LABELS[raw]) return EMAIL_TYPE_LABELS[raw];
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const humanizeReason = (reason) => {
  if (!reason) return null;
  if (reason.startsWith('client_opt_out:')) {
    const pref = reason.split(':')[1] || '';
    return `Client opted out (${pref.replace(/_/g, ' ')})`;
  }
  if (reason === 'no_email_on_file') return 'No email address on file';
  if (reason === 'send_email_disabled') return 'Sending was disabled by caller';
  return reason;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// ────────────────────────────── Component ─────────────────────────────

const ClientEmailLog = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(false);
    try {
      const result = await adminService.getClientEmailEvents(clientId, {
        days,
        eventType: filter === 'all' ? null : filter,
      });
      setData(result);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [clientId, days, filter]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-sm text-gray-500" data-testid="email-log-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading email events…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-start gap-3 p-4 border border-red-200 bg-red-50 rounded-lg"
        data-testid="email-log-error"
      >
        <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-red-900">Couldn't load email events</p>
          <p className="text-sm text-red-800 mt-1">Please try again.</p>
        </div>
        <Button variant="outline" onClick={load} className="border-red-300" data-testid="email-log-retry-btn">
          Retry
        </Button>
      </div>
    );
  }

  const counts = data?.counts || { sent: 0, skipped: 0, failed: 0 };
  const events = data?.events || [];

  return (
    <div className="space-y-4" data-testid="email-log-tab">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="gap-1 text-sm" data-testid="email-log-count-sent">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Sent: {counts.sent}
          </Badge>
          <Badge variant="outline" className="gap-1 text-sm" data-testid="email-log-count-skipped">
            <CircleSlash className="h-3.5 w-3.5 text-amber-600" /> Skipped: {counts.skipped}
          </Badge>
          <Badge variant="outline" className="gap-1 text-sm" data-testid="email-log-count-failed">
            <XCircle className="h-3.5 w-3.5 text-red-600" /> Failed: {counts.failed}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-[140px]" data-testid="email-log-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[130px]" data-testid="email-log-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              <SelectItem value="sent">Sent only</SelectItem>
              <SelectItem value="skipped">Skipped only</SelectItem>
              <SelectItem value="failed">Failed only</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            disabled={loading}
            title="Refresh"
            data-testid="email-log-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Event list */}
      {events.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 text-center"
          data-testid="email-log-empty"
        >
          <Inbox className="h-10 w-10 text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-700">No email events in this window</p>
          <p className="text-xs text-gray-500 mt-1">
            Events will appear here when the client receives invoices, payment
            confirmations, reminders, or other notifications.
          </p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y" data-testid="email-log-list">
          {events.map((evt) => {
            const badge = EVENT_BADGE[evt.event_type] || EVENT_BADGE.failed;
            const Icon = badge.Icon;
            return (
              <div
                key={evt.id}
                className="p-3 hover:bg-gray-50 transition-colors"
                data-testid={`email-log-row-${evt.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className={`gap-1 ${badge.className}`}>
                        <Icon className="h-3 w-3" />
                        {badge.label}
                      </Badge>
                      <span className="text-sm font-medium text-gray-900">
                        {humanizeEmailType(evt.email_type)}
                      </span>
                      {evt.metadata?.invoice_number && (
                        <span className="text-xs text-gray-500">· {evt.metadata.invoice_number}</span>
                      )}
                    </div>
                    {evt.subject && (
                      <p className="text-sm text-gray-700 truncate" title={evt.subject}>
                        {evt.subject}
                      </p>
                    )}
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span>{evt.email || '(no email)'}</span>
                      {evt.cc_emails?.length > 0 && (
                        <span className="text-gray-400">cc: {evt.cc_emails.join(', ')}</span>
                      )}
                    </div>
                    {evt.event_type !== 'sent' && humanizeReason(evt.reason) && (
                      <p className="text-xs text-gray-600 mt-1 italic">
                        {humanizeReason(evt.reason)}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatDateTime(evt.created_at)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data?.total_returned >= 200 && (
        <p className="text-xs text-gray-500 italic text-center">
          Showing the first 200 events. Use a shorter window or a tighter filter to narrow down.
        </p>
      )}
    </div>
  );
};

export default ClientEmailLog;
